// @ts-strict-ignore
import React, { useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgDotsHorizontalTriple } from '@actual-app/components/icons/v1';
import { SvgCheck } from '@actual-app/components/icons/v2';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { format as monthUtilFormat } from '@actual-app/core/shared/months';
import { getNormalisedString } from '@actual-app/core/shared/normalisation';
import { getScheduledAmount } from '@actual-app/core/shared/schedules';
import type { ScheduleStatuses } from '@actual-app/core/shared/schedules';
import type { ScheduleEntity } from '@actual-app/core/types/models';

import { FinancialText } from '#components/FinancialText';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { Cell, Field, Row, Table, TableHeader } from '#components/table';
import { DisplayId } from '#components/util/DisplayId';
import { useAccounts } from '#hooks/useAccounts';
import { useContextMenu } from '#hooks/useContextMenu';
import { useDateFormat } from '#hooks/useDateFormat';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { usePayees } from '#hooks/usePayees';
import { useIsNocturneTheme } from '#style';
import { getRecurringDescription } from '#util/schedule';

import { StatusBadge } from './StatusBadge';

type SchedulesTableProps = {
  isLoading?: boolean;
  schedules: readonly ScheduleEntity[];
  statuses: ScheduleStatuses;
  filter: string;
  allowCompleted: boolean;
  onSelect: (id: ScheduleEntity['id']) => void;
  style: CSSProperties;
  tableStyle?: CSSProperties;
} & (
  | {
      minimal: true;
      onAction?: never;
    }
  | {
      minimal?: false;
      onAction: (
        actionName: ScheduleItemAction,
        id: ScheduleEntity['id'],
      ) => void;
    }
);

type CompletedScheduleItem = { id: 'show-completed' };
type SchedulesTableItem = ScheduleEntity | CompletedScheduleItem;

export type ScheduleItemAction =
  | 'post-transaction'
  | 'post-transaction-today'
  | 'skip'
  | 'complete'
  | 'restart'
  | 'delete';

export const ROW_HEIGHT = 43;

function focusAdjacentScheduleRow(
  currentRow: HTMLElement,
  key: 'ArrowDown' | 'ArrowUp',
) {
  const table = currentRow.closest('[data-nocturne-schedule-table]');
  const rows = Array.from(
    table?.querySelectorAll<HTMLElement>('[data-nocturne-schedule-row]') ?? [],
  );
  const currentIndex = rows.indexOf(currentRow);
  const nextIndex = key === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1;
  rows[nextIndex]?.focus();
}

export function ScheduleAmountCell({
  amount,
  op,
  width = 100,
}: {
  amount: ScheduleEntity['_amount'];
  op: ScheduleEntity['_amountOp'];
  width?: number;
}) {
  const { t } = useTranslation();
  const isNocturneTheme = useIsNocturneTheme();
  const format = useFormat();

  const num = getScheduledAmount(amount);
  const currencyAmount = format(Math.abs(num || 0), 'financial');
  const isApprox = op === 'isapprox';
  const isBetween = op === 'isbetween';
  let cellText = '';
  if (isApprox) {
    cellText = t('Approximately {{currencyAmount}}', {
      currencyAmount,
    });
  } else if (isBetween && typeof amount != 'number') {
    cellText = t('{{currency1}} to {{currency2}}', {
      currency1: format(Math.abs(amount.num1 || 0), 'financial'),
      currency2: format(Math.abs(amount.num2 || 0), 'financial'),
    });
  } else {
    cellText = currencyAmount;
  }
  return (
    <Cell
      width={width}
      plain
      style={{
        textAlign: 'right',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '0 5px',
      }}
      name="amount"
    >
      {isApprox && (
        <View
          style={{
            textAlign: 'left',
            color: theme.pageTextSubdued,
            lineHeight: '1em',
            marginRight: 10,
          }}
          title={cellText}
        >
          ~
        </View>
      )}
      {isBetween && (
        <View
          style={{
            textAlign: 'left',
            color: theme.pageTextSubdued,
            lineHeight: '1em',
            marginRight: 10,
          }}
          title={cellText}
        >
          ±
        </View>
      )}
      <FinancialText
        style={{
          flex: 1,
          color:
            num > 0
              ? isNocturneTheme
                ? theme.pageTextPositive
                : theme.noticeTextLight
              : theme.tableText,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={cellText}
      >
        <PrivacyFilter>
          {num > 0 ? `+${currencyAmount}` : `${currencyAmount}`}
        </PrivacyFilter>
      </FinancialText>
    </Cell>
  );
}

function ScheduleRow({
  schedule,
  onAction,
  onSelect,
  minimal,
  statuses,
  dateFormat,
}: {
  schedule: ScheduleEntity;
  dateFormat: string;
} & Pick<
  SchedulesTableProps,
  'onSelect' | 'onAction' | 'minimal' | 'statuses'
>) {
  const { t } = useTranslation();
  const isNocturneTheme = useIsNocturneTheme();
  const format = useFormat();
  const locale = useLocale();
  const isNocturneLayout = isNocturneTheme && !minimal;

  const rowRef = useRef(null);
  const buttonRef = useRef(null);

  const status = statuses.get(schedule.id);
  useContextMenu({
    triggerRef: rowRef,
    items: !minimal
      ? [
          {
            name: 'post-transaction',
            text: t('Post transaction'),
            onClick: () => onAction('post-transaction', schedule.id),
          },
          {
            name: 'post-transaction-today',
            text: t('Post transaction today'),
            onClick: () => onAction('post-transaction-today', schedule.id),
          },
          {
            name: 'restart',
            text: t('Restart'),
            onClick: () => onAction('restart', schedule.id),
            hidden: status !== 'completed',
          },
          {
            name: 'skip',
            text: t('Skip next scheduled date'),
            onClick: () => onAction('skip', schedule.id),
            hidden: status === 'completed',
          },
          {
            name: 'complete',
            text: t('Complete'),
            onClick: () => onAction('complete', schedule.id),
            hidden: status === 'completed',
          },
        ]
      : [],
  });

  return (
    <Row
      ref={rowRef}
      data-nocturne-schedule-row={isNocturneTheme || undefined}
      tabIndex={isNocturneTheme ? 0 : undefined}
      role={isNocturneTheme ? 'group' : undefined}
      aria-label={
        isNocturneTheme
          ? t(
              'Edit schedule {{name}}, next {{date}}, amount {{amount}}, status {{status}}',
              {
                name: schedule.name || t('None'),
                date: schedule.next_date
                  ? monthUtilFormat(schedule.next_date, dateFormat)
                  : t('None'),
                amount: format(
                  getScheduledAmount(schedule._amount),
                  'financial',
                ),
                status: status || t('None'),
              },
            )
          : undefined
      }
      height={isNocturneTheme ? 36 : ROW_HEIGHT}
      inset={isNocturneTheme ? 22 : 15}
      onClick={() => onSelect(schedule.id)}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isNocturneTheme || event.target !== event.currentTarget) {
          return;
        }

        if (event.key === 'Enter') {
          if (event.repeat) {
            return;
          }
          event.preventDefault();
          onSelect(schedule.id);
          return;
        }
        const isBareKey =
          !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
        const canRunActionShortcut =
          isBareKey && !event.repeat && !minimal && status !== 'completed';
        if (canRunActionShortcut && event.key.toLowerCase() === 'p') {
          event.preventDefault();
          onAction('post-transaction-today', schedule.id);
          return;
        }
        if (canRunActionShortcut && event.key.toLowerCase() === 's') {
          event.preventDefault();
          onAction('skip', schedule.id);
          return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
          return;
        }

        event.preventDefault();
        focusAdjacentScheduleRow(event.currentTarget, event.key);
      }}
      style={{
        cursor: 'pointer',
        backgroundColor: theme.tableBackground,
        color: theme.tableText,
        ':hover': { backgroundColor: theme.tableRowBackgroundHover },
      }}
    >
      {isNocturneLayout ? (
        <>
          <Field width="flex" name="payee">
            {schedule._payee ? (
              <DisplayId type="payees" id={schedule._payee} />
            ) : (
              schedule.name || t('None')
            )}
          </Field>
          <Field
            width={150}
            name="frequency"
            style={{ color: theme.pageTextSubdued, fontSize: 12 }}
          >
            {schedule._date && typeof schedule._date === 'object'
              ? getRecurringDescription(schedule._date, dateFormat, locale)
              : t('Once')}
          </Field>
          <Field
            width={170}
            name="date"
            style={{ color: theme.pageTextLight, fontSize: 12 }}
          >
            {schedule.next_date
              ? monthUtilFormat(schedule.next_date, dateFormat)
              : null}
          </Field>
          <ScheduleAmountCell
            amount={schedule._amount}
            op={schedule._amountOp}
            width={130}
          />
          <Field width={120} name="status" style={{ alignItems: 'flex-end' }}>
            <StatusBadge
              status={statuses.get(schedule.id)}
              isEstimate={
                schedule._amountOp === 'isapprox' ||
                schedule._amountOp === 'isbetween'
              }
            />
          </Field>
        </>
      ) : (
        <>
          <Field width="flex" name="name">
            <Text
              style={
                schedule.name == null
                  ? { color: theme.buttonNormalDisabledText }
                  : null
              }
              title={schedule.name ? schedule.name : ''}
            >
              {schedule.name ? schedule.name : t('None')}
            </Text>
          </Field>
          <Field width="flex" name="payee">
            <DisplayId type="payees" id={schedule._payee} />
          </Field>
          <Field width="flex" name="account">
            <DisplayId type="accounts" id={schedule._account} />
          </Field>
          <Field width={110} name="date">
            {schedule.next_date
              ? monthUtilFormat(schedule.next_date, dateFormat)
              : null}
          </Field>
          <Field width={120} name="status" style={{ alignItems: 'flex-start' }}>
            <StatusBadge status={statuses.get(schedule.id)} />
          </Field>
          <ScheduleAmountCell
            amount={schedule._amount}
            op={schedule._amountOp}
          />
          {!minimal && (
            <Field width={80} style={{ textAlign: 'center' }}>
              {schedule._date &&
                typeof schedule._date === 'object' &&
                schedule._date.frequency && (
                  <SvgCheck style={{ width: 13, height: 13 }} />
                )}
            </Field>
          )}
        </>
      )}
      {!minimal && !isNocturneLayout && (
        <Field width={40} name="actions">
          <View>
            <Button
              ref={buttonRef}
              variant="bare"
              aria-label={t('Menu')}
              onPress={() => {
                if (rowRef.current) {
                  const rect = buttonRef.current?.getBoundingClientRect();
                  const clientX = rect ? rect.left : 0;
                  const clientY = rect ? rect.bottom : 0;
                  (rowRef.current as HTMLElement).dispatchEvent(
                    new MouseEvent('contextmenu', {
                      bubbles: true,
                      clientX,
                      clientY,
                    }),
                  );
                }
              }}
            >
              <SvgDotsHorizontalTriple
                width={15}
                height={15}
                style={{ transform: 'rotateZ(90deg)' }}
              />
            </Button>
          </View>
        </Field>
      )}
    </Row>
  );
}

export function SchedulesTable({
  isLoading,
  schedules,
  statuses,
  filter,
  minimal,
  allowCompleted,
  style,
  onSelect,
  onAction,
  tableStyle,
}: SchedulesTableProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const isNocturneTheme = useIsNocturneTheme();
  const isNocturneLayout = isNocturneTheme && !minimal;

  const dateFormat = useDateFormat() || 'MM/dd/yyyy';
  const [showCompleted, setShowCompleted] = useState(false);

  const { data: payees } = usePayees();
  const { data: accounts = [] } = useAccounts();

  const filteredSchedules = useMemo(() => {
    if (!filter) {
      return schedules;
    }
    const filterIncludes = (str: string) =>
      str
        ? getNormalisedString(str).includes(getNormalisedString(filter)) ||
          getNormalisedString(filter).includes(getNormalisedString(str))
        : false;

    return schedules.filter(schedule => {
      const payee = payees.find(p => schedule._payee === p.id);
      const account = accounts.find(a => schedule._account === a.id);
      const amount = getScheduledAmount(schedule._amount);
      let amountStr = '';
      if (schedule._amountOp === 'isbetween') {
        amountStr = '±';
      } else if (schedule._amountOp === 'isapprox') {
        amountStr = '~';
      }
      amountStr +=
        (amount > 0 ? '+' : '') + format(Math.abs(amount || 0), 'financial');
      const dateStr = schedule.next_date
        ? monthUtilFormat(schedule.next_date, dateFormat)
        : null;

      return (
        filterIncludes(schedule.name) ||
        filterIncludes(payee && payee.name) ||
        filterIncludes(account && account.name) ||
        filterIncludes(amountStr) ||
        filterIncludes(statuses.get(schedule.id)) ||
        filterIncludes(dateStr)
      );
    });
  }, [payees, accounts, schedules, filter, statuses, format, dateFormat]);

  const items: readonly SchedulesTableItem[] = useMemo(() => {
    const unCompletedSchedules = filteredSchedules.filter(s => !s.completed);

    if (!allowCompleted) {
      return unCompletedSchedules;
    }
    if (showCompleted) {
      return filteredSchedules;
    }

    const hasCompletedSchedule = filteredSchedules.find(s => s.completed);

    if (!hasCompletedSchedule) return unCompletedSchedules;

    return [...unCompletedSchedules, { id: 'show-completed' }];
  }, [filteredSchedules, showCompleted, allowCompleted]);

  function renderItem({ item }: { item: SchedulesTableItem }) {
    if (item.id === 'show-completed') {
      return (
        <Row
          data-nocturne-schedule-row={isNocturneTheme || undefined}
          tabIndex={isNocturneTheme ? 0 : undefined}
          role={isNocturneTheme ? 'button' : undefined}
          aria-label={
            isNocturneTheme ? t('Show completed schedules') : undefined
          }
          height={isNocturneTheme ? 36 : ROW_HEIGHT}
          inset={isNocturneTheme ? 22 : 15}
          style={{
            cursor: 'pointer',
            backgroundColor: 'transparent',
            ':hover': { backgroundColor: theme.tableRowBackgroundHover },
          }}
          onClick={() => setShowCompleted(true)}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!isNocturneTheme || event.target !== event.currentTarget) {
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              if (event.repeat) {
                return;
              }
              event.preventDefault();
              setShowCompleted(true);
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              focusAdjacentScheduleRow(event.currentTarget, event.key);
            }
          }}
        >
          <Field
            width="flex"
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              color: theme.tableText,
            }}
          >
            <Trans>Show completed schedules</Trans>
          </Field>
        </Row>
      );
    }
    return (
      <ScheduleRow
        schedule={item as ScheduleEntity}
        {...{ statuses, dateFormat, onSelect, onAction, minimal }}
      />
    );
  }

  return (
    <View
      data-nocturne-schedule-table={isNocturneTheme || undefined}
      style={{ ...styles.tableContainer, ...tableStyle }}
    >
      <TableHeader
        height={isNocturneTheme ? 36 : ROW_HEIGHT}
        inset={isNocturneTheme ? 22 : 15}
      >
        {isNocturneLayout ? (
          <>
            <Field width="flex">
              <Trans>Payee</Trans>
            </Field>
            <Field width={150}>
              <Trans>Frequency</Trans>
            </Field>
            <Field width={170}>
              <Trans>Next date</Trans>
            </Field>
            <Field width={130} style={{ textAlign: 'right' }}>
              <Trans>Amount</Trans>
            </Field>
            <Field width={120} style={{ textAlign: 'right' }}>
              <Trans>Status</Trans>
            </Field>
          </>
        ) : (
          <>
            <Field width="flex">
              <Trans>Name</Trans>
            </Field>
            <Field width="flex">
              <Trans>Payee</Trans>
            </Field>
            <Field width="flex">
              <Trans>Account</Trans>
            </Field>
            <Field width={110}>
              <Trans>Next date</Trans>
            </Field>
            <Field width={120}>
              <Trans>Status</Trans>
            </Field>
            <Field width={100} style={{ textAlign: 'right' }}>
              <Trans>Amount</Trans>
            </Field>
            {!minimal && (
              <Field width={80} style={{ textAlign: 'center' }}>
                <Trans>Recurring</Trans>
              </Field>
            )}
          </>
        )}
        {!minimal && !isNocturneLayout && <Field width={40} />}
      </TableHeader>
      <Table
        loading={isLoading}
        rowHeight={isNocturneTheme ? 36 : ROW_HEIGHT}
        backgroundColor="transparent"
        style={{ flex: 1, backgroundColor: 'transparent', ...style }}
        items={items as ScheduleEntity[]}
        renderItem={renderItem}
        renderEmpty={filter ? t('No matching schedules') : t('No schedules')}
      />
    </View>
  );
}
